import React, { useState, useMemo } from 'react';
import { Copy, Check, Download, Zap, ChevronDown, ChevronUp, Clock } from 'lucide-react';

interface ResponsePaneProps {
  isLoading: boolean;
  lastResponse: any | null;
  copied: boolean;
  onCopy: () => void;
  onExport: () => void;
  canExport: boolean;
  exportModeToggle?: React.ReactNode;
}

export function ResponsePane({ isLoading, lastResponse, copied, onCopy, onExport, canExport, exportModeToggle }: ResponsePaneProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const formattedResponse = useMemo(() => {
    if (!lastResponse) return '';
    try {
      return JSON.stringify(lastResponse, null, 2);
    } catch {
      return String(lastResponse);
    }
  }, [lastResponse]);

  const lineCount = formattedResponse.split('\n').length;
  const charCount = formattedResponse.length;
  const responseTime = lastResponse?.timestamp 
    ? new Date(lastResponse.timestamp).toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      })
    : null;

  return (
    <div className="flex flex-col h-full border-t sm:border-t-0 sm:border-l border-navy-700/30">
      {/* Header */}
      <div className="flex items-center justify-between p-2 sm:p-3 bg-navy-800/50 border-b border-navy-700/30">
        <div className="flex items-center gap-2">
          <label className="text-xs sm:text-sm font-semibold text-gray-200">
            Resposta
          </label>
          {lastResponse && (
            <>
              <span className="text-[10px] sm:text-xs text-gray-500 font-mono">
                {lineCount} linhas • {charCount} chars
              </span>
              {responseTime && (
                <span className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  {responseTime}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          {exportModeToggle}
          {lastResponse && (
            <>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs bg-gray-600/20 text-gray-400 rounded hover:bg-gray-600/30 transition-all duration-200 border border-gray-600/30"
                title={isExpanded ? 'Colapsar' : 'Expandir'}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    <span className="hidden sm:inline">Colapsar</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    <span className="hidden sm:inline">Expandir</span>
                  </>
                )}
              </button>
              <button
                onClick={onCopy}
                className="flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs bg-gray-600/20 text-gray-400 rounded hover:bg-gray-600/30 transition-all duration-200 border border-gray-600/30"
                title="Copiar resposta (Ctrl+C)"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" />
                    <span className="hidden sm:inline">Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    <span className="hidden sm:inline">Copiar</span>
                  </>
                )}
              </button>
              {lastResponse.status === 'ok' && (
                <button
                  onClick={onExport}
                  disabled={!canExport}
                  title="Exportar ação normalizada para .json"
                  className="flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs bg-blue-600/20 text-blue-400 rounded hover:bg-blue-600/30 transition-all duration-200 border border-blue-600/30 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Download className="w-3 h-3" />
                  <span className="hidden sm:inline">Exportar</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-navy-900/50">
        {isLoading ? (
          <div className="flex items-center justify-center h-full min-h-[200px] text-gray-400">
            <div className="flex flex-col items-center gap-3">
              <div className="w-6 h-6 border-[3px] border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
              <span className="text-xs sm:text-sm font-medium">Aguardando resposta...</span>
            </div>
          </div>
        ) : lastResponse ? (
          <div className="p-3 space-y-3">
            {/* Status Badge */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-3 py-1.5 rounded text-xs sm:text-sm font-bold ${
                lastResponse.status === 'ok' 
                  ? 'bg-green-600/20 text-green-400 border-2 border-green-500/40'
                  : lastResponse.status === 'erro'
                  ? 'bg-red-600/20 text-red-400 border-2 border-red-500/40'
                  : 'bg-blue-600/20 text-blue-400 border-2 border-blue-500/40'
              }`}>
                {lastResponse.status.toUpperCase()}
              </span>
              {lastResponse.requestId && (
                <span className="text-[10px] sm:text-xs text-gray-500 font-mono bg-navy-800/50 px-2 py-1 rounded">
                  ID: {lastResponse.requestId.substring(0, 12)}...
                </span>
              )}
            </div>

            {/* JSON Response */}
            <div className={`bg-navy-800/50 border border-navy-700/50 rounded ${isExpanded ? '' : 'max-h-96'} overflow-auto`}>
              <pre 
                className="text-xs sm:text-sm text-gray-300 font-mono p-3 sm:p-4 leading-relaxed whitespace-pre-wrap break-words"
                style={{ 
                  tabSize: 2,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {formattedResponse}
              </pre>
            </div>

            {/* Message if exists */}
            {lastResponse.message && (
              <div className="bg-blue-600/10 border border-blue-600/30 rounded p-2.5">
                <p className="text-xs sm:text-sm text-blue-300">{lastResponse.message}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full min-h-[200px] text-gray-500">
            <div className="text-center">
              <Zap className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-3 opacity-50" />
              <p className="text-xs sm:text-sm font-medium mb-1">Nenhuma resposta</p>
              <p className="text-[10px] sm:text-xs">Envie uma ação para ver a resposta aqui</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


